<div
  class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-ink/80 px-4 py-10"
  (click)="close()"
>
  <div
    class="w-full max-w-lg rounded-md border border-hairline bg-panel p-6"
    (click)="$event.stopPropagation()"
  >
    <div class="mb-4 flex items-start justify-between gap-4">
      <h2 class="font-display text-xl font-semibold text-parchment">
        {{ isCreate() ? 'Create user' : (user()?.displayName ?? 'User') }}
      </h2>
      <button
        type="button"
        (click)="close()"
        class="text-xl leading-none text-dusk hover:text-parchment"
        aria-label="Close"
      >
        &times;
      </button>
    </div>

    @if (loading()) {
      <p class="text-dusk">Loading…</p>
    } @else if (errorMessage()) {
      <p class="rounded border border-wax/40 bg-wax/10 px-4 py-3 text-sm text-parchment">
        {{ errorMessage() }}
      </p>
    } @else {
      <form [formGroup]="form" (ngSubmit)="save()" class="flex flex-col gap-3">
        <label class="flex flex-col gap-1 text-sm">
          <span class="text-dusk">Email</span>
          <input
            formControlName="email"
            type="email"
            class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
          />
        </label>

        <label class="flex flex-col gap-1 text-sm">
          <span class="text-dusk">Display name</span>
          <input
            formControlName="displayName"
            type="text"
            class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
          />
        </label>

        @if (isCreate()) {
          <label class="flex flex-col gap-1 text-sm">
            <span class="text-dusk">Password</span>
            <app-password-field>
              <input
                formControlName="password"
                appPasswordInput
                class="w-full rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none pr-11"
              />
            </app-password-field>
          </label>
        }

        <div class="grid grid-cols-2 gap-3">
          <label class="flex flex-col gap-1 text-sm">
            <span class="text-dusk">Site role</span>
            <select
              formControlName="siteRole"
              class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
            >
              <option value="USER">User</option>
              <option value="SITE_ADMIN">Site admin</option>
            </select>
          </label>
          <label class="flex flex-col gap-1 text-sm">
            <span class="text-dusk">Plan</span>
            <select
              formControlName="plan"
              class="rounded border border-hairline bg-ink px-3 py-2 text-parchment focus:border-gold focus:outline-none"
            >
              <option value="FREE">Free</option>
              <option value="TIER_1">Tier 1</option>
              <option value="SITE_ADMIN">Site Admin (no limits)</option>
            </select>
          </label>
        </div>

        @if (saveError()) {
          <p class="rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
            {{ saveError() }}
          </p>
        }

        <div class="mt-2 flex items-center gap-3">
          <button
            type="submit"
            [disabled]="saving() || form.invalid"
            class="inline-flex items-center justify-center rounded-[3px] bg-gradient-to-b from-gold to-gold-dim px-5 py-2 text-sm font-bold text-ink transition hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
          >
            {{ saving() ? 'Saving…' : (isCreate() ? 'Create' : 'Save') }}
          </button>
        </div>
      </form>

      @if (!isCreate()) {
        <div class="mt-6 border-t border-hairline pt-4">
          <h3 class="text-sm font-semibold uppercase tracking-wide text-dusk">Activity</h3>
          <dl class="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
            <dt class="text-dusk">Created</dt>
            <dd class="text-parchment" data-testid="created-at">
              {{ user()?.createdAt | date: 'MMM d, y, h:mm a' }}
            </dd>
            <dt class="text-dusk">First login</dt>
            <dd class="text-parchment" data-testid="first-login">
              {{ (user()?.firstLoginAt | date: 'MMM d, y, h:mm a') ?? 'Never' }}
            </dd>
            <dt class="text-dusk">Last login</dt>
            <dd class="text-parchment" data-testid="last-login">
              {{ (user()?.lastLoginAt | date: 'MMM d, y, h:mm a') ?? 'Never' }}
            </dd>
          </dl>
        </div>

        <div class="mt-6 border-t border-hairline pt-4">
          <h3 class="text-sm font-semibold uppercase tracking-wide text-dusk">Password</h3>
          <p class="mt-2 text-xs text-dusk">
            Sends this user a password reset link by email — they'll set their own new
            password, same as the "Forgot it?" flow. Sending it also marks their email as
            verified, so they won't be asked to verify it when they sign in.
          </p>
          <button
            type="button"
            (click)="sendPasswordReset()"
            [disabled]="sendingReset()"
            class="mt-3 inline-flex items-center justify-center rounded-[3px] border border-hairline px-4 py-2 text-xs font-semibold text-parchment transition hover:border-parchment disabled:cursor-not-allowed disabled:opacity-60"
          >
            {{ sendingReset() ? 'Sending…' : 'Send password reset email' }}
          </button>
          @if (resetSent()) {
            <span class="ml-3 text-xs text-gold">Sent.</span>
          }
          @if (resetError()) {
            <p class="mt-2 rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
              {{ resetError() }}
            </p>
          }
        </div>

        <div class="mt-6 border-t border-hairline pt-4">
          @if (deleteError()) {
            <p class="mb-3 rounded border border-wax/40 bg-wax/10 px-3 py-2 text-xs text-parchment">
              {{ deleteError() }}
            </p>
          }
          @if (!confirmingDelete()) {
            <button
              type="button"
              (click)="confirmingDelete.set(true)"
              class="text-xs text-wax-bright hover:underline"
            >
              Delete this user
            </button>
          } @else {
            <div class="flex items-center gap-3 text-xs">
              <span class="text-parchment">Delete permanently? This can't be undone.</span>
              <button
                type="button"
                (click)="confirmDelete()"
                [disabled]="deleting()"
                class="rounded-[3px] border border-wax-bright px-3 py-1.5 font-semibold text-wax-bright transition hover:bg-wax/10 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {{ deleting() ? 'Deleting…' : 'Yes, delete' }}
              </button>
              <button
                type="button"
                (click)="confirmingDelete.set(false)"
                class="text-dusk hover:text-parchment"
              >
                Cancel
              </button>
            </div>
          }
        </div>
      }
    }
  </div>
</div>
